// Summary: Consumer-only bridge to the host mobile app's IPOSGO tap-on-phone terminal; strict witness parser + local-dev simulation.
// Details: docs/function-library/web/services.md#tap-on-phone-bridge

import { logToBackend } from '../client-log/client-log.service';

export type TapPaymentOutcome = 'approved' | 'declined' | 'cancelled';

export interface TapPaymentResult {
  outcome: TapPaymentOutcome;
  transactionId: string;
  approvalNumber: string | null;
  cardLast4: string | null;
  cardBrand: string | null;
  amount: number | null;
  message: string | null;
}

interface NativeBridge {
  warmUpTerminal?: () => void;
  chargeCard?: (amount: number, currency: string) => void;
}

interface TopBridgeStartMessage {
  action: 'startPayment';
  data: { amount: string };
  timestamp: string;
}

interface NativePostMessage {
  postMessage: (message: TopBridgeStartMessage) => void;
}

declare global {
  interface Window {
    AndroidBridge?: NativeBridge;
    mobileApp?: NativePostMessage;
  }
}

// CRA inlines literal process.env.REACT_APP_* reads at build time; declared here so TS compiles in the browser.
declare const process: { env: Record<string, string | undefined> };

// Odoo decline contract: any of these statuses (or a non-"00" response code) marks an explicit decline.
const DECLINE_STATUSES = new Set([
  'declined', 'denied', 'rejected', 'refused', 'refuse',
  'failed', 'fail', 'cancelled', 'canceled', 'cancel',
  'aborted', 'abort', 'error', 'not_approved', 'notapproved',
]);
const NATIVE_TIMEOUT_MS = 90000;
const SIMULATED_DELAY_MS = 1200;

type Resolver = (result: TapPaymentResult) => void;

// Summary: Reads a deep property path off an unknown object without throwing.
// Details: docs/function-library/web/services.md#tap-on-phone-bridge
const readPath = (source: unknown, path: string[]): unknown => {
  let current: unknown = source;
  for (const key of path) {
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return current;
};

const asString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

class TapOnPhoneBridge {
  private pendingResolver: Resolver | null = null;
  private active = false;
  private listenerBound = false;
  private simulatedTimer: number | null = null;
  private nativeTimer: number | null = null;
  // Raw messageFromNative payloads captured during the current attempt, for diagnosing how the device responded.
  private capturedMessages: unknown[] = [];

  // Summary: Raw native messages received during the most recent tap attempt (diagnostic for unhandled device responses).
  // Details: docs/function-library/web/services.md#tap-on-phone-bridge
  getDiagnostics(): unknown[] {
    return this.capturedMessages.slice();
  }

  // Summary: True when a real native bridge is present or local-dev simulation applies.
  // Details: docs/function-library/web/services.md#tap-on-phone-bridge
  isAvailable(): boolean {
    return this.hasNativeBridge() || this.isSimulated();
  }

  private hasNativeBridge(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    // Odoo hasTopBridge contract: host counts only when it exposes a callable postMessage or warmUpTerminal.
    return (
      typeof window.mobileApp?.postMessage === 'function' ||
      typeof window.AndroidBridge?.warmUpTerminal === 'function'
    );
  }

  private isSimulated(): boolean {
    if (typeof window === 'undefined' || this.hasNativeBridge()) {
      return false;
    }
    // Simulation is opt-in only: without an explicit flag a device with no native bridge must hide tap-on-phone (Odoo parity).
    return process.env.REACT_APP_TAP_BRIDGE_SIM === '1';
  }

  // Summary: Starts a tap charge and resolves once the native host reports a terminal (approved/declined) outcome.
  // Details: docs/function-library/web/services.md#tap-on-phone-bridge
  startPayment(amount: number): Promise<TapPaymentResult> {
    if (this.active) {
      return Promise.reject(new Error('A tap payment is already in progress'));
    }
    this.active = true;
    this.capturedMessages = [];
    logToBackend('tap-on-phone', 'startPayment', { amount });

    if (this.isSimulated()) {
      return this.simulatePayment(amount);
    }

    this.bindListener();
    return new Promise<TapPaymentResult>((resolve) => {
      this.pendingResolver = resolve;
      // Safety net mirroring Odoo's 90s bridge timeout so a silent host never leaves the charge spinning forever.
      this.nativeTimer = window.setTimeout(() => {
        this.nativeTimer = null;
        if (!this.active) {
          return;
        }
        this.active = false;
        this.pendingResolver = null;
        logToBackend('tap-on-phone', 'native-timeout-declined', { messages: this.capturedMessages }, 'warn');
        resolve({ outcome: 'declined', transactionId: '', approvalNumber: null, cardLast4: null, cardBrand: null, amount: null, message: null });
      }, NATIVE_TIMEOUT_MS);
      this.invokeNativeStart(amount);
    });
  }

  cancelPayment(): void {
    if (typeof window !== 'undefined') {
      if (this.simulatedTimer !== null) {
        window.clearTimeout(this.simulatedTimer);
      }
      if (this.nativeTimer !== null) {
        window.clearTimeout(this.nativeTimer);
      }
    }
    this.simulatedTimer = null;
    this.nativeTimer = null;
    // Settle (never drop) the in-flight promise so the awaiting charge unwinds cleanly instead of hanging or firing late.
    const resolve = this.pendingResolver;
    if (this.active) {
      logToBackend('tap-on-phone', 'cancelPayment', { messages: this.capturedMessages });
    }
    this.active = false;
    this.pendingResolver = null;
    resolve?.({ outcome: 'cancelled', transactionId: '', approvalNumber: null, cardLast4: null, cardBrand: null, amount: null, message: null });
  }

  private invokeNativeStart(amount: number): void {
    if (typeof window === 'undefined') {
      return;
    }
    // Odoo outbound contract: object (not stringified) postMessage first, Android chargeCard as the fallback.
    if (typeof window.mobileApp?.postMessage === 'function') {
      window.mobileApp.postMessage({ action: 'startPayment', data: { amount: String(amount) }, timestamp: new Date().toISOString() });
      return;
    }
    if (typeof window.AndroidBridge?.chargeCard === 'function') {
      window.AndroidBridge.chargeCard(amount, 'ILS');
    }
  }

  private bindListener(): void {
    if (this.listenerBound || typeof window === 'undefined') {
      return;
    }
    this.listenerBound = true;
    // Odoo inbound contract: a single messageFromNative DOM event whose detail carries the paymentResult payload.
    window.addEventListener('messageFromNative', (event) =>
      this.handleNativeMessage((event as CustomEvent).detail),
    );
  }

  private handleNativeMessage(payload: unknown): void {
    if (!this.active) {
      // A late message after the attempt already settled — log it so we can see post-resolution traffic.
      logToBackend('tap-on-phone', 'message-after-settled', { payload }, 'warn');
      return;
    }
    // Capture every raw device message so an unhandled/declined response can be inspected verbatim.
    this.capturedMessages.push(payload);
    const parsed = this.parseMessage(payload);
    logToBackend('tap-on-phone', 'native-message', { payload, decision: parsed ? parsed.outcome : 'non-final' });
    if (!parsed) {
      return;
    }
    if (this.nativeTimer !== null && typeof window !== 'undefined') {
      window.clearTimeout(this.nativeTimer);
      this.nativeTimer = null;
    }
    const resolve = this.pendingResolver;
    this.active = false;
    this.pendingResolver = null;
    resolve?.(parsed);
  }

  // Summary: Odoo paymentResult parser — ignores non-paymentResult/interim messages, declines on bad status or response code, approves only on success + response_code "00" + a card-digit witness.
  // Details: docs/function-library/web/services.md#tap-on-phone-bridge
  private parseMessage(payload: unknown): TapPaymentResult | null {
    const data = typeof payload === 'string' ? this.tryJson(payload) : payload;
    if (data === null || typeof data !== 'object') {
      return null;
    }
    const envelope = data as Record<string, unknown>;

    // Only paymentResult events are terminal; any other action is ignored.
    const action = asString(envelope.action);
    if (action && action !== 'paymentResult') {
      return null;
    }

    // The host may nest the real payload under `success` (object or stringified JSON), else the envelope itself is the result.
    let result: Record<string, unknown> = envelope;
    const successField = envelope.success;
    if (successField !== null && typeof successField === 'object') {
      result = successField as Record<string, unknown>;
    } else if (typeof successField === 'string') {
      const parsedSuccess = this.tryJson(successField);
      if (parsedSuccess !== null && typeof parsedSuccess === 'object') {
        result = parsedSuccess as Record<string, unknown>;
      }
    }

    const txResult =
      (result.transactionResult as Record<string, unknown> | undefined) ??
      (envelope.transactionResult as Record<string, unknown> | undefined) ??
      {};
    const de48 = (readPath(txResult, ['customMessage', 'DE48']) as Record<string, unknown> | undefined) ?? {};

    const status = (asString(result.status) ?? asString(envelope.status) ?? '').toLowerCase();
    const responseCodeRaw = result.response_code != null ? result.response_code : envelope.response_code;
    const responseCode = responseCodeRaw != null ? String(responseCodeRaw) : '';

    const message = asString(result.message) ?? asString(envelope.message) ?? asString(envelope.customMessage) ?? null;
    const successFlag = result.success === true || envelope.success === true;
    const lowerMessage = (message ?? '').toLowerCase();

    // SDK sometimes wraps an APPROVED EMV transaction with success:false; recognise it as a real success.
    const emvOverrideOk =
      result.success !== true &&
      txResult.emvAccepted === true &&
      txResult.kernelResult === 'EMV_ONLINE_ACCEPT' &&
      (responseCode === '00' || status === 'approved');

    const hasApprovalSignals =
      emvOverrideOk ||
      successFlag ||
      status === 'approved' ||
      responseCode === '00';

    // Card-read-failed can be a transient SDK text even on approved payloads; only treat it as interim when no approval signal exists.
    if ((lowerMessage.includes('card read') || lowerMessage.includes('card_read')) && !hasApprovalSignals) {
      return null;
    }

    const transactionId =
      asString(result.transaction_id) ??
      asString(result.transactionId) ??
      asString(envelope.transaction_id) ??
      asString(envelope.transactionId) ??
      '';
    const approvalNumber =
      asString(result.approvalNumber) ??
      asString(envelope.approvalNumber) ??
      asString(de48.ApprovalNumber);
    const cardBrand =
      asString(result.card_brand) ??
      asString(result.cardBrand) ??
      asString(envelope.card_brand) ??
      asString(envelope.cardBrand) ??
      asString(txResult.cardType) ??
      asString(de48.CardName) ??
      asString(result.cardType) ??
      asString(envelope.cardType);
    const amount = this.toAmount(result.amount ?? envelope.amount);

    // Explicit decline: a known decline status OR a non-empty, non-"00" response code (unless EMV override approved it).
    const declined = !emvOverrideOk && (DECLINE_STATUSES.has(status) || (responseCode !== '' && responseCode !== '00'));
    if (declined) {
      return { outcome: 'declined', transactionId, approvalNumber, cardLast4: null, cardBrand, amount, message };
    }

    const cardLast4 = this.extractCard4(result, envelope, txResult, de48);
    const isSuccess = emvOverrideOk || result.success === true || envelope.success === true || status === 'approved';

    // Strict authorization: success AND response_code "00" AND a card-digit witness; anything weaker is non-final, so keep waiting.
    if (isSuccess && responseCode === '00' && cardLast4) {
      return { outcome: 'approved', transactionId, approvalNumber, cardLast4, cardBrand, amount, message };
    }
    return null;
  }

  // Summary: Returns the first 4-digit group from any known card-number field (flat ZCredit, nested EMV, DE48).
  // Details: docs/function-library/web/services.md#tap-on-phone-bridge
  private extractCard4(
    result: Record<string, unknown>,
    envelope: Record<string, unknown>,
    txResult: Record<string, unknown>,
    de48: Record<string, unknown>,
  ): string | null {
    const candidates = [
      result.maskedPan,
      txResult.maskedPan,
      de48.Card4Digits,
      de48.CardNumber,
      result.card_last_4,
      result.last_4_digits,
      result.last4,
      envelope.maskedPan,
      envelope.card_last_4,
    ];
    for (const candidate of candidates) {
      const match = (asString(candidate) ?? '').match(/\d{4}/);
      if (match) {
        return match[0];
      }
    }
    return null;
  }

  private tryJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private toAmount(value: unknown): number | null {
    const text = asString(value);
    if (text === null) {
      return null;
    }
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private simulatePayment(amount: number): Promise<TapPaymentResult> {
    return new Promise<TapPaymentResult>((resolve) => {
      this.pendingResolver = resolve;
      this.simulatedTimer = window.setTimeout(() => {
        this.simulatedTimer = null;
        if (!this.active) {
          return;
        }
        this.active = false;
        this.pendingResolver = null;
        resolve({
          outcome: 'approved',
          transactionId: `SIM-TOP-${Date.now()}`,
          approvalNumber: null,
          cardLast4: '4242',
          cardBrand: 'Visa',
          amount,
          message: null,
        });
      }, SIMULATED_DELAY_MS);
    });
  }
}

export const tapOnPhoneBridge = new TapOnPhoneBridge();
